import React, { useEffect, useState } from 'react';
import { ResponseDisposition, ReviewResponseEntry } from './types';

// How each comment was handled — tells the researcher at a glance which ones still need
// action in InfoEd.
const DISPOSITION_LABELS: Record<ResponseDisposition, string> = {
  revised: 'Revised here',
  draft_to_paste: 'Paste into InfoEd',
  infoed: 'Change in InfoEd',
  no_change: 'Explained'
};

// Plain-text point-by-point letter in committee order (the server sorts the entries), in
// the Comment / Response shape reviewers read against the revised protocol.
export function formatLetter(entries: ReviewResponseEntry[], roundNumber: number): string {
  const title = `Response to IACUC Review Comments${roundNumber > 1 ? ` (Round ${roundNumber})` : ''}`;
  const body = entries
    .map((e) => `${e.commentRef}. ${e.commentSummary}\nResponse: ${e.responseText}`)
    .join('\n\n');
  return `${title}\n\n${body}\n`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers / non-secure contexts: fall back to a hidden textarea.
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

interface ResponseLetterProps {
  entries: ReviewResponseEntry[];
  roundNumber: number;
}

// Review Response sidebar card: one line per comment the agent has recorded, plus a wide
// overlay with the full letter and a Copy button. Entries come from review_responses, so
// the letter survives a refresh and is never regenerated from the chat.
const ResponseLetter: React.FC<ResponseLetterProps> = ({ entries, roundNumber }) => {
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(formatLetter(entries, roundNumber));
    setCopyState(ok ? 'copied' : 'failed');
    window.setTimeout(() => setCopyState('idle'), 2000);
  };

  return (
    <section className="agent-letter">
      <h3>Response Letter</h3>
      {entries.length === 0 ? (
        <p className="agent-progress-hint">
          Each comment gets an entry here as the agent works through it: a point-by-point reply
          your reviewers can check against the revised protocol.
        </p>
      ) : (
        <>
          <ul className="agent-letter-list">
            {entries.map((e) => (
              <li key={e.commentRef} className="agent-letter-item" title={e.commentSummary}>
                <span className="agent-letter-ref">{e.commentRef}</span>
                <span className="agent-letter-summary">{e.commentSummary}</span>
                <span className={`agent-letter-disp ${e.disposition}`}>
                  {DISPOSITION_LABELS[e.disposition] || e.disposition}
                </span>
              </li>
            ))}
          </ul>
          <button className="agent-progress-open" onClick={() => setOpen(true)}>
            View &amp; copy letter ({entries.length})
          </button>
        </>
      )}

      {open && (
        <div className="agent-letter-overlay" onClick={() => setOpen(false)}>
          <div
            className="agent-letter-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Response letter"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="agent-letter-modal-header">
              <h3>
                Response to IACUC Review Comments{roundNumber > 1 ? ` (Round ${roundNumber})` : ''}
              </h3>
              <div className="agent-letter-modal-actions">
                <button className="agent-chat-btn-primary" onClick={handleCopy}>
                  {copyState === 'copied' ? 'Copied ✓' : copyState === 'failed' ? 'Copy failed' : 'Copy letter'}
                </button>
                <button className="agent-chat-btn-secondary" onClick={() => setOpen(false)}>
                  Close
                </button>
              </div>
            </div>
            <ol className="agent-letter-entries">
              {entries.map((e) => (
                <li key={e.commentRef} className="agent-letter-entry">
                  <div className="agent-letter-entry-head">
                    <strong>{e.commentRef}. {e.commentSummary}</strong>
                    <span className={`agent-letter-disp ${e.disposition}`}>
                      {DISPOSITION_LABELS[e.disposition] || e.disposition}
                    </span>
                  </div>
                  {e.sections.length > 0 && (
                    <div className="agent-letter-sections">Section{e.sections.length > 1 ? 's' : ''} {e.sections.join(', ')}</div>
                  )}
                  <p className="agent-letter-response">{e.responseText}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
};

export default ResponseLetter;
